import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { MIME_TYPE } from "common/constants";
import { getPath } from "common/routing";
import { useDispatchBulkDocumentUploadCreateScriptJobMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import {
  Container,
  FormContainer,
  Section,
} from "../../../Common/__styles__/FullPageFormLayout";
import FullPageFormLayout from "../../../Common/FullPageFormLayout";
import { Body, Title } from "../../../Common/Typography";
import { FormTitleWrapper, List, ListItem } from "./__styles__/Operations";
import {
  AccountDocumentTypeSelect,
  BaseDispatchForm,
  InputFile,
  useDispatchFileUpload,
} from "./shared";

type DispatchBulkDocumentUploadActionForm = BaseDispatchForm & {
  accountDocumentTypeId: string;
};

export const DispatchBulkDocumentUploadActionForm = () => {
  const formMethods = useForm<DispatchBulkDocumentUploadActionForm>();

  const history = useHistory();
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { uploadFiles, isUploading } =
    useDispatchFileUpload<DispatchBulkDocumentUploadActionForm>(formMethods);
  const [dispatch, { loading: isDispatching }] =
    useDispatchBulkDocumentUploadCreateScriptJobMutation({
      onCompleted: () => {
        addSuccessToast("Dispatched bulk document upload operation!");
        history.push(getPath("operations"));
      },
      onError: () => {
        addErrorToast("Failed to dispatch bulk document upload operation.");
      },
    });

  const { handleSubmit, watch } = formMethods;

  const fileUploadValue = watch("inputCsv");
  const missingRequiredField = isEmpty(fileUploadValue);

  const onSubmit = async (formData: DispatchBulkDocumentUploadActionForm) => {
    const uploadResult = await uploadFiles();

    if (!uploadResult) {
      return;
    }

    await dispatch({
      variables: {
        data: {
          ...uploadResult,
          accountDocumentTypeId: formData.accountDocumentTypeId,
        },
      },
    });
  };

  return (
    <FullPageFormLayout
      subtitle="Bulk document upload actions"
      prevLocation={getPath("operations")}
      buttonLoading={isUploading || isDispatching}
      onSave={handleSubmit(onSubmit)}
      buttonDisabled={missingRequiredField}
    >
      <Container>
        <FormContainer>
          <FormProvider {...formMethods}>
            <form>
              <FormTitleWrapper>
                <Title size="large" type="semiBold">
                  Bulk document upload actions
                </Title>
              </FormTitleWrapper>
              <Body size={"default"} type={"regular"} color="contentSecondary">
                This form will allow you to bulk upload non-EC document types.
                <br />
                <br />
                When the script is complete you will receive an email with a
                link to download a CSV containing any errors that occurred
                during the operation.
              </Body>
              <FormTitleWrapper>
                <Title size={"small"} type={"regular"}>
                  Source file
                </Title>
              </FormTitleWrapper>
              <List style={{ margin: 0 }}>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    The first row of your CSV should contain the column headers
                  </Body>
                </ListItem>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    Required column:
                  </Body>
                  <pre>originalFilename</pre>
                </ListItem>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    Optional column:
                  </Body>
                  <pre>issuedAt</pre>
                </ListItem>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    There must be geocoding columns provided and they must come
                    in at least one of the following combinations:
                  </Body>
                  <pre>propertyId</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>objectId</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>fullAddress</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>fullAddress, latitude, longitude</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>streetAddress, city, state, zip</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>
                    streetAddress, city, state, zip, latitude, longitude
                  </pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>streetNumber, streetName, suffix, city, state, zip</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>
                    streetNumber, streetName, suffix, city, state, zip,
                    latitude, longitude
                  </pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>latitude, longitude</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>parcelNumber</pre>
                  <Body type="emphasis" size="default" color="contentSecondary">
                    OR
                  </Body>
                  <pre>block, lot</pre>
                  <Body
                    type="regular"
                    size="default"
                    color="contentSecondary"
                    style={{ marginTop: "8px" }}
                  >
                    Note: propertyId and objectId cannot both be set on the same
                    row. When latitude/longitude are provided alongside address
                    fields, they will be used if address geocoding fails.
                  </Body>
                </ListItem>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    You must either provide a column for the file type OR select
                    an account document type below. If provided, file type
                    column name should be:
                  </Body>
                  <pre>fileType</pre>
                </ListItem>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    You must either provide a url column, a filename column for
                    files that already exist in the account, OR upload a zip of
                    files alongside a filename column that references the file
                    names in the zip. If provided, url / filename column names
                    should be:
                  </Body>
                  <pre>url</pre>
                  <pre>originalFilename</pre>
                </ListItem>
              </List>
              <FormTitleWrapper>
                <Title size={"small"} type={"regular"}>
                  Associated files
                </Title>
              </FormTitleWrapper>
              <List style={{ margin: 0 }}>
                <ListItem>
                  <Body type="regular" size="default" color="contentSecondary">
                    If providing an associated files zip, files names must match
                    the name in the originalFilename column{" "}
                    <strong>exactly</strong>. This is both case and character
                    sensitive.
                  </Body>
                </ListItem>
              </List>
              <Section style={{ marginTop: "16px", marginBottom: "16px" }}>
                <AccountDocumentTypeSelect description='The Account Document Type to create uploads for. If not selected here, the csv file MUST have a "fileType" column.' />
                <InputFile
                  name="inputCsv"
                  label="Source file"
                  required={true}
                  description="The csv file with the uploads to create"
                  allowedMimeTypes={[MIME_TYPE.CSV]}
                />
                <InputFile
                  name="inputZip"
                  required={false}
                  label="Associated files"
                  description="If providing additional files for this record, please include all files in a zip. Not required if the CSV has a url column."
                  allowedMimeTypes={[MIME_TYPE.ZIP]}
                />
              </Section>
            </form>
          </FormProvider>
        </FormContainer>
      </Container>
    </FullPageFormLayout>
  );
};
